import React from 'react';
import { useApp } from './appContext';
import { signOutCurrentSession } from './signOut';

// Sits between AuthGate and the app. Non-admins see a lockout screen while maintenance mode is on;
// admins pass through (they're the ones doing the maintenance) and get a persistent banner so it's
// obvious the site is closed to everyone else.
//
// This is a courtesy, NOT the enforcement -- MaintenanceGuard on the API is what actually stops a
// non-admin mutating data during a lockout. Hiding the UI alone would be trivially bypassable.
// Rendered in both trees, so it must not reach for MSAL: the portal has no MsalProvider above it
// (index.js), and useMsal() outside one throws.
function LockoutScreen({ message }) {
  return (
    <div style={{ maxWidth: 520, margin: '0 auto', paddingTop: 96, textAlign: 'center' }}>
      <div style={{ fontSize: 40, marginBottom: 16 }}>🔧</div>
      <div style={{ fontFamily: 'Georgia', fontSize: 22, fontWeight: 'bold', color: 'var(--heading)', marginBottom: 8 }}>
        Down for maintenance
      </div>
      <div style={{ fontSize: 14, color: 'var(--text-mid)', lineHeight: 1.6, marginBottom: 24 }}>
        Pulse is temporarily unavailable while we make some changes. Please check back
        shortly.
      </div>
      {message && (
        <div style={{
          padding: '14px 18px', borderRadius: 8, marginBottom: 24, textAlign: 'left',
          background: 'rgba(251,176,64,0.08)', border: '1px solid rgba(251,176,64,0.3)',
          fontSize: 13, color: 'var(--warn-text)', whiteSpace: 'pre-wrap',
        }}>
          {message}
        </div>
      )}
      <button className="btn btn-outline" onClick={() => signOutCurrentSession()}>
        Sign out
      </button>
    </div>
  );
}

export function MaintenanceBanner() {
  const { maintenance } = useApp();
  if (!maintenance?.maintenanceMode) return null;

  return (
    <div style={{
      padding: '8px 24px', background: 'var(--yellow)', color: '#5c3d00',
      fontSize: 12, fontWeight: 700, textAlign: 'center',
    }}>
      ⚠ Maintenance mode is ON — everyone except admins is locked out of the app.
    </div>
  );
}

export default function MaintenanceGate({ children }) {
  const { loading, error, currentUser, maintenance } = useApp();

  if (loading) {
    return (
      <div style={{ textAlign: 'center', paddingTop: 96, color: 'var(--text-mid)', fontSize: 13 }}>
        Loading…
      </div>
    );
  }

  // Fail OPEN on error, deliberately. If /auth/me or /maintenance is unreachable we can't tell
  // whether maintenance is on or whether this user is an admin -- and locking everyone out because
  // the API hiccuped would be worse than letting them through to a UI whose calls the API will
  // reject anyway (the guard there is the real enforcement, and it fails closed).
  if (error) {
    return (
      <>
        <div style={{
          padding: '8px 24px', background: 'rgba(239,65,54,0.08)',
          borderBottom: '1px solid rgba(239,65,54,0.3)', fontSize: 12, color: 'var(--red)',
          textAlign: 'center',
        }}>
          ⚠ Couldn't reach the API ({error}). Some features may not work.
        </div>
        {children}
      </>
    );
  }

  if (maintenance?.maintenanceMode && currentUser?.role !== 'admin') {
    return <LockoutScreen message={maintenance.maintenanceMessage} />;
  }

  return children;
}
